import React, { useState, useEffect, useRef, useCallback, MouseEvent } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Doctor, Patient, ValidateTokenResponse } from '../../types/api';
import { apiService } from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiError';
import { mapStoredUserToDoctor, parseStoredUser } from '../../utils/doctorUser';
import { isAccessTokenValidInStorage } from '../../utils/teleSession';
import DispensePage from '../dispense/DispensePage';
import SingpassLoginButton from '../../components/SingpassLoginButton';
import { singpassLoginError, singpassStaffNotice } from '../../utils/singpassLoginError';

type ConsultStep = 'validating' | 'expired' | 'login' | 'signature' | 'consult';

function ExternalConsultPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<ConsultStep>('validating');
  const [visitULID, setVisitULID] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [visitLockWarning, setVisitLockWarning] = useState<string>('');
  const [loginError, setLoginError] = useState('');
  const [singpassLoading, setSingpassLoading] = useState(false);

  // ===================== TOKEN VALIDATION =====================
  useEffect(() => {
    if (!token) {
      setErrorMessage('No consult token provided.');
      setStep('expired');
      return;
    }

    const validateToken = async () => {
      try {
        const result: ValidateTokenResponse = await apiService.validateConsultToken(token);
        if (result.valid && result.visitULID) {
          setVisitULID(result.visitULID);
          const resumeToken = sessionStorage.getItem('drTele_externalSingpassResume');
          const returningFromThisLink = resumeToken === token;
          if (returningFromThisLink) {
            sessionStorage.removeItem('drTele_externalSingpassResume');
          }

          const signedInUser = isAccessTokenValidInStorage() ? parseStoredUser() : null;
          if (signedInUser && (returningFromThisLink || signedInUser.roleId === 2)) {
            if (signedInUser.roleId !== 2) {
              localStorage.removeItem('accessToken');
              localStorage.removeItem('user');
              setLoginError('This consult link is for doctors. Sign in with the doctor Singpass account.');
              setStep('login');
              return;
            }
            const doctorInfo = mapStoredUserToDoctor(signedInUser);
            if (result.pickedByEmpId && result.pickedByEmpId !== doctorInfo.id) {
              setVisitLockWarning(
                `This visit is currently picked up by ${result.pickedByDoctorName || 'another doctor'}. You can still open it; some edits may be restricted for the assigned doctor.`
              );
            } else {
              setVisitLockWarning('');
            }
            setDoctor(doctorInfo);
            setStep('signature');
            return;
          }
          setStep('login');
        } else {
          setErrorMessage(getApiErrorMessage({ response: { data: { message: result.message } } }, 'This consult link is invalid or has expired.'));
          setStep('expired');
        }
      } catch (err: any) {
        console.error('Token validation error:', err);
        setErrorMessage('Unable to validate consult link. Please try again.');
        setStep('expired');
      }
    };

    validateToken();
  }, [token]);

  useEffect(() => {
    const code = singpassLoginError(searchParams.get('singpass'));
    if (!code) return;
    setLoginError(singpassStaffNotice(code, false));
  }, [searchParams]);

  const handleSingpass = async () => {
    setLoginError('');
    if (!token) {
      setLoginError('Consult link is missing.');
      return;
    }
    setSingpassLoading(true);
    try {
      sessionStorage.setItem('drTele_externalSingpassResume', token);
      const authUrl = await apiService.getDoctorSingpassAuthUrl();
      window.location.href = authUrl;
    } catch (err: unknown) {
      sessionStorage.removeItem('drTele_externalSingpassResume');
      console.error('Singpass error:', err);
      setLoginError(getApiErrorMessage(err, 'Unable to start Singpass login. Please try again.'));
      setSingpassLoading(false);
    }
  };

  // ===================== SIGNATURE STEP =====================
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const drawingPointsRef = useRef(0);
  const totalPathLengthRef = useRef(0);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [isSignatureValid, setIsSignatureValid] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sigError, setSigError] = useState<string | null>(null);

  const validateSignature = useCallback(() => {
    const isValid = drawingPointsRef.current >= 30 && totalPathLengthRef.current >= 200;
    setIsSignatureValid(isValid);
    return isValid;
  }, []);

  useEffect(() => {
    if (step !== 'signature') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      if (canvas.width !== rect.width * dpr || canvas.height !== rect.height * dpr) {
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, rect.width, rect.height);
      }
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    let lastTouchPos: { x: number; y: number } | null = null;

    const getTouchPos = (e: globalThis.TouchEvent) => {
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0] || e.changedTouches[0];
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    };

    const handleTouchStart = (e: globalThis.TouchEvent) => {
      e.preventDefault();
      const pos = getTouchPos(e);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      isDrawingRef.current = true;
      setIsDrawing(true);
      drawingPointsRef.current++;
      lastTouchPos = pos;
    };

    const handleTouchMove = (e: globalThis.TouchEvent) => {
      e.preventDefault();
      if (!isDrawingRef.current) return;
      const pos = getTouchPos(e);
      if (lastTouchPos) {
        totalPathLengthRef.current += Math.sqrt(
          (pos.x - lastTouchPos.x) ** 2 + (pos.y - lastTouchPos.y) ** 2
        );
      }
      lastTouchPos = pos;
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      drawingPointsRef.current++;
      setHasDrawn(true);
      validateSignature();
    };

    const handleTouchEnd = () => {
      isDrawingRef.current = false;
      setIsDrawing(false);
    };

    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });
    canvas.addEventListener('touchcancel', handleTouchEnd, { passive: false });

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [step, validateSignature]);

  const getPos = (e: MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDrawing = (e: MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
    drawingPointsRef.current++;
  };

  const draw = (e: MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e);
    if (lastPosRef.current) {
      totalPathLengthRef.current += Math.sqrt(
        (pos.x - lastPosRef.current.x) ** 2 + (pos.y - lastPosRef.current.y) ** 2
      );
    }
    lastPosRef.current = pos;
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    drawingPointsRef.current++;
    setHasDrawn(true);
    validateSignature();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    lastPosRef.current = null;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasDrawn(false);
    setIsSignatureValid(false);
    drawingPointsRef.current = 0;
    totalPathLengthRef.current = 0;
    lastPosRef.current = null;
  };

  const handleSaveSignature = async () => {
    if (!hasDrawn || !validateSignature() || !doctor?.id) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    try {
      setIsSaving(true);
      setSigError(null);

      const base64Data = canvas.toDataURL('image/png').split(',')[1];
      const response = await apiService.saveSignature({
        empID: doctor.id,
        multiUse: true,
        signatureData: base64Data,
      });

      if (response.hasSignature) {
        // Load the patient/visit details and proceed to consult
        try {
          const details = await apiService.getPatientDetails(visitULID);
          const patientData: Patient = {
            id: 0,
            visitULID: visitULID,
            queueNo: '',
            name: details.fullName || '',
            nric: details.nric || '',
            age: details.age || 0,
            gender: (details.gender === 'M' ? 'M' : 'F') as 'M' | 'F',
            store: details.storeName || '',
            storeId: details.storeID || 0,
            visitTime: '',
            temp: details.temperature ?? 0,
            sbp: details.sbp ?? 0,
            dbp: details.dbp ?? 0,
            pulse: details.pulse ?? 0,
            spo2: details.spo2 ?? 0,
            weight: details.weight ?? 0,
            height: details.height ?? 0,
            waist: details.waist ?? 0,
            remarks: details.remarks || '',
            allergies: details.allergies || [],
            chronicConditions: details.chronicConditions || [],
            walkIn: details.walkIn,
            teleMed: details.teleMed,
            corporateCompanyName: details.corporateCompanyName,
          };
          setPatient(patientData);
          setStep('consult');
        } catch (err: any) {
          console.error('Error loading visit details:', err);
          setSigError('Failed to load visit details. Please try again.');
        }
      } else {
        setSigError('Failed to save signature. Please try again.');
      }
    } catch (err: any) {
      console.error('Error saving signature:', err);
      setSigError(getApiErrorMessage(err, 'Could not save signature. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  // ===================== CONSULT COMPLETE (CLOSE) =====================
  const handleConsultClose = () => {
    // Close the browser tab/window after consult is complete
    // Show a thank-you message briefly, then close
    document.title = 'Consult Complete';
    
    // Try to close the window
    try {
      window.close();
    } catch {
      // If window.close() doesn't work (e.g. not opened by script),
      // show a message to the user
    }
    
    // Fallback: replace page content with a thank-you message
    setStep('validating'); // Reuse validating step to show a message
    setErrorMessage('__CLOSED__'); // Special flag
  };

  // ===================== RENDER =====================

  // Validating / Loading
  if (step === 'validating') {
    if (errorMessage === '__CLOSED__') {
      return (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '100vh', background: 'linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)'
        }}>
          <div style={{
            background: '#fff', borderRadius: 20, padding: '48px 40px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)', textAlign: 'center', maxWidth: 440
          }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
            <h2 style={{ color: '#059669', marginBottom: 12 }}>Consult Complete</h2>
            <p style={{ color: '#64748b', fontSize: 15 }}>
              You may now close this browser tab.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '100vh', background: 'linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)'
      }}>
        <div style={{
          background: '#fff', borderRadius: 20, padding: '48px 40px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)', textAlign: 'center', maxWidth: 440
        }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⏳</div>
          <h2 style={{ color: '#1e293b', marginBottom: 12 }}>Validating Consult Link...</h2>
          <p style={{ color: '#64748b', fontSize: 15 }}>Please wait while we verify your link.</p>
        </div>
      </div>
    );
  }

  // Expired / Invalid
  if (step === 'expired') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '100vh', background: 'linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)'
      }}>
        <div style={{
          background: '#fff', borderRadius: 20, padding: '48px 40px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)', textAlign: 'center', maxWidth: 440
        }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🔒</div>
          <h2 style={{ color: '#dc2626', marginBottom: 12 }}>Link Expired</h2>
          <p style={{ color: '#64748b', fontSize: 15, lineHeight: 1.6 }}>
            {errorMessage}
          </p>
          <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 16 }}>
            Please contact the clinic to request a new consult link.
          </p>
        </div>
      </div>
    );
  }

  // Login
  if (step === 'login') {
    return (
      <div className="login-container">
        <div className="login-box">
          <h1 className="login-title">External Consult</h1>
          <p className="login-subtitle">Sign in with Singpass to open this visit</p>
          {loginError && (
            <div style={{ color: '#f87171', marginBottom: 16, fontSize: 14 }}>{loginError}</div>
          )}
          <SingpassLoginButton
            variant="white"
            disabled={singpassLoading}
            loading={singpassLoading}
            loadingLabel="Redirecting to Singpass..."
            onClick={() => void handleSingpass()}
          />
        </div>
      </div>
    );
  }

  // Signature
  if (step === 'signature') {
    return (
      <div className="signature-container">
        <div className="signature-box">
          <h1 className="login-title">Signature Required</h1>
          <p className="login-subtitle">
            Welcome, {doctor?.name}. Please sign below.
          </p>
          {visitLockWarning ? (
            <div
              style={{
                marginBottom: 16,
                padding: '10px 12px',
                background: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: 8,
                color: '#92400e',
                fontSize: 13,
                lineHeight: 1.5,
                textAlign: 'left',
              }}
            >
              {visitLockWarning}
            </div>
          ) : null}
          <canvas
            ref={canvasRef}
            className="signature-canvas"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
          />
          <div className="signature-actions">
            <button className="btn btn-secondary" onClick={clearCanvas} disabled={isSaving}>
              Clear
            </button>
            <button
              className={`btn btn-primary ${!isSignatureValid && hasDrawn ? 'btn-warning' : ''}`}
              onClick={handleSaveSignature}
              disabled={!hasDrawn || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save & Continue'}
            </button>
          </div>
          {sigError && (
            <div style={{
              marginTop: 12, padding: '8px 12px', background: '#fee2e2',
              border: '1px solid #dc2626', borderRadius: 6, color: '#991b1b',
              fontSize: 13, textAlign: 'center'
            }}>
              {sigError}
            </div>
          )}
          {hasDrawn && !isSignatureValid && (
            <div style={{
              marginTop: 12, padding: '8px 12px', background: '#fef3c7',
              border: '1px solid #f59e0b', borderRadius: 6, color: '#92400e',
              fontSize: 13, textAlign: 'center'
            }}>
              Signature insufficient. Please continue signing.
            </div>
          )}
        </div>
      </div>
    );
  }

  // Consult (reuse DispensePage with no back/queue nav)
  if (step === 'consult' && doctor && patient) {
    return (
      <>
        {visitLockWarning ? (
          <div
            style={{
              padding: '10px 16px',
              background: '#fef3c7',
              borderBottom: '1px solid #f59e0b',
              color: '#92400e',
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {visitLockWarning}
          </div>
        ) : null}
        <DispensePage
          doctor={doctor}
          patient={patient}
          onBack={handleConsultClose}
          onLogout={handleConsultClose}
          isExternalConsult={true}
        />
      </>
    );
  }

  return null;
}

export default ExternalConsultPage;
